import React, { useCallback, useMemo } from 'react'
import { useTableStyles } from '../../../shared/styles/table.styles'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { ChargingSessionMobileTableRowItem } from './ChargingSessionMobileTableRowItem'
import { Paper } from '@mui/material'
import { ChargingSessionModel } from '../../models/charging-sessions.model'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'
import useModals from 'src/Hooks/useModals'
import { useChargingSessionTableHeader } from '../../hooks/use-e-mobility-charging-session-table-header'
import { mobileSortFn } from '../../../shared/utils/sort-mobile-table'

interface ChargingSessionMobileTableViewProps {
  chargingSession: ChargingSessionModel[]
  shouldDisplayMidMeter: boolean
}

/**
 * Displays information equivalent to a regular table, but optimized for smaller screens.
 * Instead of displaying multiple columns with large amount of content in each row, it
 * instead displays one simple row which can optionally be expanded to reveal additional
 * session details.
 */
export const ChargingSessionMobileTableView: React.FC<ChargingSessionMobileTableViewProps> = ({ chargingSession, shouldDisplayMidMeter }) => {
  const { classes, cx } = useTableStyles()
  const { openChargingSessionDetails } = useModals()

  const chargingSessionFilters = useTypedSelector(state => state.eMobility.chargingSessionsFilters.filters)
  const dispatch = useDispatch()

  const sortBy = chargingSessionFilters?.sort?.name ?? 'startDateTime'
  const sortDirection = chargingSessionFilters?.sort?.direction ?? 'desc'
  const desktopHeader = useChargingSessionTableHeader({ shouldDisplayMidMeter })

  const header = useMemo<Header<ChargingSessionModel>[]>(
    () => [
      {
        /**
         * We need to sort sessions by latest first, but don't need the whole sorting functionality seen in regular desktop
         * table. For this reason, we "abuse" the value field to let TableComplete sort by "startDateTime", but render a
         * completely different label("Wallbox") to adhere to provided design.
         */
        value: sortBy,
        isSortable: false,
        sortFunction: (item1, item2, columnName) => {
          const sortFn = desktopHeader.find(headerItem => headerItem.value === columnName)?.sortFunction

          if (!sortFn) {
            return mobileSortFn(item1, item2)
          }

          return sortFn(item1, item2)
        },
        label: '' /** mobile view does not show a header at all, only rows */,
        valueRender: (_, __, chargingSession) => {
          if (!chargingSession) return <></>

          return <ChargingSessionMobileTableRowItem chargingSession={chargingSession} />
        },
      },
    ],
    [sortBy, desktopHeader]
  )

  const handleRowSelect = useCallback(
    (session: ChargingSessionModel) => {
      openChargingSessionDetails({
        session,
        shouldDisplayMidMeter,
      })
    },
    [openChargingSessionDetails, shouldDisplayMidMeter]
  )

  return (
    <Paper id="charging-session-mobile-item" sx={{ p: 0 }}>
      <TableComplete
        key={`${sortBy}-${sortDirection}`}
        header={header}
        data={chargingSession}
        onSelect={handleRowSelect}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        rowClassName={classes.row}
        className={cx(classes.table, classes.tableMobile)}
        bodyClassName={classes.chargingSessionMobileTableBody}
        headerClassName={cx(classes.tableHeader, classes.tableHeaderMobile)}
        setResultLimit={value => dispatch(updateChargingSessionsFilters({ ...chargingSessionFilters, limit: value, page: 0 }))}
        resultLimit={chargingSessionFilters?.limit ?? 10}
        resultPage={chargingSessionFilters?.page ?? 0}
        setResultPage={value => dispatch(updateChargingSessionsFilters({ ...chargingSessionFilters, page: value }))}
        initialSort={{
          name: sortBy,
          direction: chargingSessionFilters?.sort?.direction ?? 'desc',
        }}
      />
    </Paper>
  )
}
